import React, {
  Component,
  ErrorInfo,
  createContext,
  lazy,
  Suspense,
  useContext,
  useState,
  useMemo,
} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, GlobalStyles, Box, Button, Typography, useMediaQuery } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import apiService from './services/api';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { createAppTheme, tokens } from './theme/theme';
import './i18n';
import i18n from './i18n';
import Navigation from './components/layout/Navigation';
import MobileBottomNav from './components/layout/MobileBottomNav';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import LoadingSpinner from './components/common/LoadingSpinner';
import AdminRoute from './components/auth/AdminRoute';
import VerifyEmailPage from './components/auth/VerifyEmailPage';
import AdminLayout from './components/admin/AdminLayout';
import AiTransparencyFooter from './components/common/AiTransparencyFooter';

const HomePage = lazy(() => import('./components/pages/HomePage'));
const FindProvidersPage = lazy(() => import('./components/pages/FindProvidersPage'));
const MyBookingsPage = lazy(() => import('./components/pages/MyBookingsPage'));
const OpportunitiesPage = lazy(() => import('./components/pages/OpportunitiesPage'));
const ProfilePage = lazy(() => import('./components/pages/ProfilePage'));
const ProviderDashboardPage = lazy(() => import('./components/pages/ProviderDashboardPage'));
const MessagingPage = lazy(() => import('./components/pages/MessagingPage'));
const PaymentsPage = lazy(() => import('./components/pages/PaymentsPage'));
const MyReviewsPage = lazy(() => import('./components/pages/MyReviewsPage'));
const NotificationsPage = lazy(() => import('./components/pages/NotificationsPage'));
const ForgotPasswordPage = lazy(() => import('./components/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./components/pages/ResetPasswordPage'));
const AdminDashboardPage = lazy(() => import('./components/pages/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('./components/pages/AdminUsersPage'));
const AdminProvidersPage = lazy(() => import('./components/pages/AdminProvidersPage'));
const AdminReviewsPage = lazy(() => import('./components/pages/AdminReviewsPage'));
const AdminSettingsPage = lazy(() => import('./components/pages/AdminSettingsPage'));
const AdminDisputesPage = lazy(() => import('./components/pages/AdminDisputesPage'));
const AdminQuoteRequestsPage = lazy(() => import('./components/pages/AdminQuoteRequestsPage'));
const MemoryPage = lazy(() => import('./components/pages/MemoryPage'));

// ─── Color mode context ────────────────────────────────────────────────────────
export const ColorModeContext = createContext({
  mode: 'light' as 'light' | 'dark',
  toggleColorMode: () => {},
});
export const useColorMode = () => useContext(ColorModeContext);

// ─── Grain texture overlay (adds depth to cream/dark backgrounds) ──────────────
const grainStyles = {
  'body::before': {
    content: '""',
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 9999,
    opacity: 0.035,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
    backgroundSize: '200px 200px',
  },
} as const;

// ─── Error boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught error:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Box sx={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: tokens.color.cream, px: 3,
        }}>
          <Box sx={{ maxWidth: 480, textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: tokens.font.display,
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 500, lineHeight: 1.1, mb: 2,
              color: tokens.color.earth,
            }}>
              {i18n.t('common:error_boundary.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
              {i18n.t('common:error_boundary.message')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => this.setState({ error: null })}
              sx={{
                bgcolor: tokens.color.terra,
                '&:hover': { bgcolor: tokens.color.terraDark },
                borderRadius: tokens.radius.full,
                px: 4,
              }}
            >
              {i18n.t('common:error_boundary.retry')}
            </Button>
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}

// ─── React Query client ────────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

// ─── Protected route ───────────────────────────────────────────────────────────
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (requireAuth && !isAuthenticated) return <Navigate to={redirectTo} replace />;
  if (!requireAuth && isAuthenticated) {
    const dest = user?.userType === 'provider' ? '/dashboard' : '/';
    return <Navigate to={dest} replace />;
  }

  return <>{children}</>;
};

// ─── Page transition wrapper ───────────────────────────────────────────────────
const pageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -6 },
};
const pageTransition = { duration: 0.22, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] };

// Legacy `/quotes` → unified Bookings hub. Preserves the query string so old
// notification deep-links (?quoteId=…) still land on and highlight the quote.
const QuotesRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/bookings${search}`} replace />;
};

// ─── App content (needs Router context) ───────────────────────────────────────
const AppContent: React.FC = () => {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Seeds the money formatter with the deployment's currency and locale. Fetched
  // once here (rather than per screen) because formatMoney is used from plain
  // helpers as well as components.
  //
  // `isPending` is read deliberately. TanStack Query only re-renders on result
  // properties that were actually accessed, and configureMoney writes to module
  // state that React does not observe — so ignoring the result would let a
  // configured EUR deployment render BRL amounts until some unrelated re-render.
  // Reading it both subscribes this component and gives us something to gate on.
  const { isPending: configPending } = useQuery({
    queryKey: ['app-config'],
    queryFn: () => apiService.getAppConfig(),
    staleTime: Infinity,
  });

  const isAdminRoute = location.pathname.startsWith('/admin');
  const theme = useMediaQuery('(max-width:899px)'); // md breakpoint
  const isMobile = theme;
  const isAiPoweredRoute = ['/providers', '/bookings', '/reviews', '/memory'].some(
    path => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  // Nothing renders before the currency is known, so no screen can paint an amount
  // in the wrong currency and then silently correct itself. getAppConfig swallows
  // failures and resolves to defaults, so this cannot hang on a bad response.
  if (loading || configPending) return <LoadingSpinner />;

  // Mobile bottom nav adds 56px; add padding so content isn't hidden under it
  const bottomPad = isMobile && isAuthenticated && !isAdminRoute ? '56px' : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isAdminRoute && <Navigation />}
      <Box component={isAdminRoute ? 'div' : 'main'} sx={{ flexGrow: 1, pb: bottomPad }}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
            style={{ display: 'contents' }}
          >
            <Suspense fallback={<LoadingSpinner />}>
            <Routes location={location}>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<ProtectedRoute requireAuth={false}><LoginForm /></ProtectedRoute>} />
              <Route path="/register" element={<ProtectedRoute requireAuth={false}><RegisterForm /></ProtectedRoute>} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Protected */}
              <Route path="/providers"     element={<ProtectedRoute><FindProvidersPage /></ProtectedRoute>} />
              <Route path="/bookings"      element={<ProtectedRoute><MyBookingsPage /></ProtectedRoute>} />
              <Route path="/bookings/:id"  element={<Navigate to="/bookings" replace />} />
              <Route path="/opportunities" element={<ProtectedRoute><OpportunitiesPage /></ProtectedRoute>} />
              <Route path="/profile"       element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/dashboard"     element={<ProtectedRoute><ProviderDashboardPage /></ProtectedRoute>} />
              <Route path="/messages"      element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
              <Route path="/payments"      element={<ProtectedRoute><PaymentsPage /></ProtectedRoute>} />
              <Route path="/quotes"        element={<ProtectedRoute><QuotesRedirect /></ProtectedRoute>} />
              <Route path="/reviews"       element={<ProtectedRoute><MyReviewsPage /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/memory"        element={<ProtectedRoute><MemoryPage /></ProtectedRoute>} />

              {/* Admin — own layout, no top Navigation */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="users"     element={<AdminUsersPage />} />
                <Route path="providers" element={<AdminProvidersPage />} />
                <Route path="reviews"   element={<AdminReviewsPage />} />
                <Route path="disputes"  element={<AdminDisputesPage />} />
                <Route path="quote-requests" element={<AdminQuoteRequestsPage />} />
                <Route path="settings"  element={<AdminSettingsPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </Box>
      {!isAdminRoute && isAuthenticated && isAiPoweredRoute && <AiTransparencyFooter />}
      {!isAdminRoute && <MobileBottomNav />}
    </Box>
  );
};

// ─── Root app — owns color mode state ─────────────────────────────────────────
const App: React.FC = () => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('colorMode') as 'light' | 'dark') || 'light';
  });

  const colorModeContextValue = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('colorMode', next);
          return next;
        });
      },
    }),
    [mode]
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorModeContextValue}>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <GlobalStyles styles={grainStyles} />
            <CssBaseline />
            <Router>
              <AuthProvider>
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: mode === 'dark' ? tokens.color.nightCard : '#fff',
                      color: mode === 'dark' ? '#F0EBE3' : '#1A1410',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                      borderRadius: '12px',
                      border: `1px solid ${mode === 'dark' ? tokens.color.nightBorder : tokens.color.paperDark}`,
                    },
                    success: {
                      iconTheme: { primary: tokens.color.earth, secondary: '#fff' },
                    },
                    error: {
                      iconTheme: { primary: '#C0392B', secondary: '#fff' },
                    },
                  }}
                />
              </AuthProvider>
            </Router>
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ColorModeContext.Provider>
  );
};

export default App;
